"use client";

import React from 'react';
import { formatarData } from "@/lib/utils";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/atoms/dialog";

interface EventoHeaderProps {
    eventoData: {
        documentId?: string;
        title?: string;
        description?: string;
        publishedAt?: string;
        content?: string;
        access_evento?: string;
        acess_evento?: string;
        image?: {
            url?: string;
            alternativeText?: string;
        };
        capa_video?: {
            url?: string;
            alternativeText?: string;
        };
        tipo_evento?: string;
        url_video?: string;
        data_evento?: string;
        data_evento_inicio?: string;
        data_evento_fim?: string;
        hora_evento?: string;
        hora_evento_inicio?: string;
        hora_evento_fim?: string;
        local_evento?: string;
        email_evento?: string;
        telefone?: string;
        cms_estado_evento?: {
            label?: string;
            color_code?: string;
            state?: string;
        };
        estado_evento?: {
            color_code?: string;
            state?: string;
        };
    };
}

const EventoHeader: React.FC<EventoHeaderProps> = ({ eventoData }) => {
    const [isVideoModalOpen, setIsVideoModalOpen] = React.useState(false);

    // Função auxiliar para determinar a cor do estado do evento
    const getEstadoEventoColor = (estado?: { color_code?: string; state?: string }) => {
        if (!estado) return '#82B662';

        switch (estado.state?.toUpperCase()) {
            case 'REALIZADO':
                return '#22C55E';
            case 'CANCELADO':
                return '#EF4444';
            case 'EM ANDAMENTO':
                return '#F59E0B';
            case 'ADIADO':
                return '#8B5CF6';
            default:
                return estado.color_code || '#82B662';
        }
    };
    console.log("[EVENTO HEADER] eventoData:", getEstadoEventoColor(eventoData.estado_evento));

    const InfoCard = ({ icon: Icon, value }: { icon: React.ReactNode; value: string }) => (
        <div className="px-4 py-8 bg-white rounded-sm shadow-[0px_8px_20px_0px_rgba(139,141,142,0.15)] inline-flex justify-center items-center gap-4">
            <div className="flex-shrink-0">
                {Icon}
            </div>
            <div className="justify-center text-neutral-500 text-2xl font-semibold font-['Nunito'] leading-6">
                {value}
            </div>
        </div>
    );

    // Função para converter URL do YouTube para embed
    const getYouTubeEmbedUrl = (url: string) => {
        if (!url) return '';

        // Se já for URL de embed, retorna como está
        if (url.includes('youtube.com/embed/')) {
            return url;
        }

        // Extrair ID do vídeo de diferentes formatos de URL
        let videoId = '';

        // Formato: https://www.youtube.com/watch?v=VIDEO_ID
        if (url.includes('youtube.com/watch?v=')) {
            videoId = url.split('watch?v=')[1]?.split('&')[0];
        }
        // Formato: https://youtu.be/VIDEO_ID
        else if (url.includes('youtu.be/')) {
            videoId = url.split('youtu.be/')[1]?.split('?')[0];
        }

        // Retornar URL de embed
        return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
    };

    return (
        <div className='container mx-auto px-4 sm:px-6 lg:px-8'>
            {/* Layout principal - 12 colunas: Descrição (5 cols) à esquerda, vídeo (7 cols) à direita */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-8">
                {/* Descrição - lado esquerdo - 5 colunas */}
                <div className="lg:col-span-5 flex flex-col justify-center order-2 lg:order-1">
                    <div
                        className="prose prose-sm sm:prose-base max-w-none  text-base font-normal  leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: eventoData.description || eventoData.content || "" }}
                    />
                </div>

                {/* Vídeo - lado direito - 7 colunas */}
                {(eventoData.url_video || eventoData.capa_video?.url) ? (
                    <div className="lg:col-span-7 flex flex-col justify-center order-1 lg:order-2">
                        {/* Sempre mostra a capa_video (thumbnail) - ao clicar abre o modal */}
                        {eventoData.capa_video?.url && (
                            <>
                                <div
                                    className="relative w-full aspect-video rounded-xl overflow-hidden bg-gray-50 group cursor-pointer shadow-lg"
                                    onClick={() => setIsVideoModalOpen(true)}
                                >
                                    <img
                                        src={eventoData.capa_video.url}
                                        alt={eventoData.capa_video.alternativeText || 'Thumbnail do vídeo'}
                                        className="w-full h-full object-cover"
                                    />
                                    {/* Ícone de Play */}
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
                                        <div className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                                            <svg
                                                className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 text-gray-800 ml-1"
                                                fill="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path d="M8 5v14l11-7z"/>
                                            </svg>
                                        </div>
                                    </div>
                                </div>

                                {/* Modal com o vídeo */}
                                <Dialog open={isVideoModalOpen} onOpenChange={setIsVideoModalOpen}>
                                    <DialogContent className="max-w-5xl w-full p-0">
                                        <DialogHeader className="sr-only">
                                            <DialogTitle>Vídeo do evento</DialogTitle>
                                        </DialogHeader>
                                        <div className="relative w-full aspect-video bg-black">
                                            <iframe
                                                src={eventoData.url_video ? getYouTubeEmbedUrl(eventoData.url_video) : ''}
                                                title="Vídeo do evento"
                                                className="w-full h-full"
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                allowFullScreen
                                            />
                                        </div>
                                    </DialogContent>
                                </Dialog>
                            </>
                        )}
                    </div>
                ) : (
                    <div className="lg:col-span-7 flex flex-col justify-center order-1 lg:order-2">
                        {/* Espaço vazio cinza quando não há vídeo */}
                        <div className="w-full aspect-video rounded-xl bg-gray-200" />
                    </div>
                )}
            </div>

            {/* Cards de informação - sobrepostos ao vídeo - 12 colunas */}
            <div className="mb-12 sm:mb-16 lg:mb-20 lg:-mt-12 relative z-10">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                    {/* Card de Data */}
                    {(eventoData.data_evento_inicio || eventoData.data_evento) && (
                        <InfoCard
                            icon={
                                <svg width="35" height="35" viewBox="0 0 35 35" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1.5 12.1667H33.5M8.61111 1.5V5.05556M26.3889 1.5V5.05556M6.83333 17.5H10.3889M15.7222 17.5H19.2778M24.6111 17.5H28.1667M6.83333 22.8333H10.3889M15.7222 22.8333H19.2778M24.6111 22.8333H28.1667M6.83333 28.1667H10.3889M15.7222 28.1667H19.2778M24.6111 28.1667H28.1667M7.18889 33.5H27.8111C29.8024 33.5 30.7981 33.5 31.5587 33.1124C32.2276 32.7716 32.7716 32.2276 33.1124 31.5587C33.5 30.7981 33.5 29.8024 33.5 27.8111V10.7444C33.5 8.75314 33.5 7.75749 33.1124 6.99692C32.7716 6.32789 32.2276 5.78396 31.5587 5.44309C30.7981 5.05556 29.8024 5.05556 27.8111 5.05556H7.18889C5.1976 5.05556 4.20194 5.05556 3.44137 5.44309C2.77234 5.78396 2.22841 6.32789 1.88754 6.99692C1.5 7.75749 1.5 8.75314 1.5 10.7444V27.8111C1.5 29.8024 1.5 30.7981 1.88754 31.5587C2.22841 32.2276 2.77234 32.7716 3.44137 33.1124C4.20194 33.5 5.19758 33.5 7.18889 33.5Z" stroke="#F15A29" strokeWidth="3" strokeLinecap="round"/>
                                </svg>
                            }
                            value={(() => {
                                const dataInicio = eventoData.data_evento_inicio || eventoData.data_evento;
                                const dataFim = eventoData.data_evento_fim;

                                if (!dataInicio) return "Data não disponível";

                                const formattedInicio = formatarData(dataInicio);

                                if (dataFim && dataFim !== dataInicio) {
                                    return `${formattedInicio} - ${formatarData(dataFim)}`;
                                }

                                return formattedInicio;
                            })()}
                        />
                    )}

                    {/* Card de Acesso */}
                    {(eventoData.acess_evento || eventoData.access_evento) && (
                        <InfoCard
                            icon={
                                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g clipPath="url(#clip0_3804_40315)">
                                        <path d="M23.313 26.1034L17.017 22.6154C19.357 20.7744 19.993 17.1564 19.993 15.1274V10.9044C19.993 8.10838 16.278 4.99438 12.546 4.99438C8.816 4.99438 5.002 8.10838 5.002 10.9044V15.1274C5.002 16.9724 5.782 20.7034 8.146 22.5994L1.688 26.1024C1.688 26.1024 0 26.8544 0 27.7914V30.3254C0 31.2584 0.757 32.0144 1.688 32.0144H23.313C24.244 32.0144 25.001 31.2574 25.001 30.3254V27.7914C25.001 26.7974 23.312 26.1024 23.312 26.1024L23.313 26.1034ZM23.001 30.0164H2V28.2284C2.143 28.1234 2.344 28.0024 2.502 27.9304C2.549 27.9094 2.596 27.8864 2.641 27.8604L9.1 24.3574C9.689 24.0374 10.079 23.4454 10.139 22.7784C10.199 22.1114 9.92 21.4584 9.398 21.0394C7.721 19.6944 7.002 16.7174 7.002 15.1284V10.9054C7.002 9.46838 9.71 6.99538 12.546 6.99538C15.435 6.99538 17.993 9.43538 17.993 10.9054V15.1284C17.993 16.6944 17.507 19.6854 15.781 21.0434C15.253 21.4594 14.968 22.1134 15.024 22.7824C15.08 23.4514 15.47 24.0494 16.059 24.3714L22.355 27.8594C22.41 27.8894 22.481 27.9224 22.539 27.9484C22.687 28.0114 22.868 28.1154 23.001 28.2074V30.0164ZM30.312 21.1244L23.922 17.6364C26.262 15.7954 26.992 12.1774 26.992 10.1484V5.92537C26.992 3.12937 23.184 -0.015625 19.452 -0.015625C17.027 -0.015625 14.548 1.30338 13.105 2.99137C13.928 3.04238 14.835 3.04338 15.619 3.29338C16.673 2.47237 18.005 1.98537 19.452 1.98537C22.341 1.98537 24.992 4.45538 24.992 5.92638V10.1494C24.992 11.7154 24.412 14.7064 22.687 16.0644C22.158 16.4804 21.874 17.1344 21.93 17.8034C21.986 18.4734 22.375 19.0704 22.965 19.3924L29.355 22.8804C29.41 22.9104 29.481 22.9434 29.539 22.9694C29.687 23.0324 29.868 23.1364 30.001 23.2284V25.0074H25.964C26.574 25.4674 26.758 26.1254 26.995 27.0074H30.314C31.245 27.0074 32.002 26.2504 32.002 25.3184V22.8154C32.001 21.8204 30.312 21.1244 30.312 21.1244Z" fill="#F15A29"/>
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_3804_40315">
                                            <rect width="32" height="32" fill="white"/>
                                        </clipPath>
                                    </defs>
                                </svg>
                            }
                            value={eventoData.acess_evento || eventoData.access_evento || ""}
                        />
                    )}

                    {/* Card de Local */}
                    {eventoData.local_evento && (
                        <InfoCard
                            icon={
                                <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M5.20512 17.0967C2.80256 16.2419 1.60128 15.8143 1.24336 15.1661C0.933201 14.6042 0.918687 13.9258 1.20455 13.3513C1.53443 12.6882 2.71635 12.2098 5.08018 11.2531L27.3612 2.23456C29.6892 1.29222 30.8533 0.821064 31.5887 1.06264C32.227 1.27233 32.7276 1.77294 32.9374 2.41124C33.179 3.14663 32.7077 4.31068 31.7655 6.63879L22.747 28.9197C21.7902 31.2836 21.3118 32.4656 20.6486 32.7955C20.0742 33.0813 19.3957 33.0668 18.8339 32.7566C18.1856 32.3987 17.7581 31.1974 16.9032 28.7948L14.2859 21.439C14.1196 20.9716 14.0365 20.7379 13.9006 20.5428C13.7803 20.3698 13.6302 20.2197 13.4572 20.0994C13.262 19.9635 13.0283 19.8804 12.5609 19.7141L5.20512 17.0967Z" stroke="#F15A29" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            }
                            value={eventoData.local_evento}
                        />
                    )}

                    {/* Card de Telefone */}
                    {eventoData.telefone && (
                        <InfoCard
                            icon={
                                <svg width="20" height="32" viewBox="0 0 20 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M2.85714 2.90909H18.5714C18.9503 2.90909 19.3137 3.06234 19.5816 3.33512C19.8495 3.6079 20 3.97787 20 4.36364V30.5455C20 30.9312 19.8495 31.3012 19.5816 31.574C19.3137 31.8468 18.9503 32 18.5714 32H1.42857C1.04969 32 0.686328 31.8468 0.418419 31.574C0.15051 31.3012 0 30.9312 0 30.5455V0H2.85714V2.90909ZM2.85714 13.0909H17.1429V5.81818H2.85714V13.0909ZM2.85714 16V29.0909H17.1429V16H2.85714Z" fill="#F15A29"/>
                                </svg>
                            }
                            value={eventoData.telefone}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default EventoHeader;